import React from 'react'
import type { MedicalProviderInfoProps } from '@/features/enrollment'

export default function MedicalProviderInfo({ enrollmentData, updateEnrollmentData, errors }: MedicalProviderInfoProps) {
  return (
    <>
      {/* Medical Provider Diagnosis */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Did a medical provider diagnosis and/or treat you for any of the gaming related injuries? <span className="text-red-500">*</span>
          {enrollmentData.medicalProviderDiagnosis === 'No' && (
            <span className="ml-2 text-xs text-gray-500">
              (Auto-selected: No)
            </span>
          )}
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input 
              type="radio" 
              name="medicalProviderDiagnosis" 
              value="Yes" 
              checked={enrollmentData.medicalProviderDiagnosis === 'Yes'} 
              onChange={() => updateEnrollmentData('medicalProviderDiagnosis', 'Yes')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="medicalProviderDiagnosis" 
              value="No" 
              checked={enrollmentData.medicalProviderDiagnosis === 'No'} 
              onChange={() => updateEnrollmentData('medicalProviderDiagnosis', 'No')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">No</span>
          </label>
        </div>
        {errors.medicalProviderDiagnosis && (
          <p className="mt-1 text-sm text-red-600">{errors.medicalProviderDiagnosis}</p>
        )}
      </div>

      {/* Medication for Gaming Injuries */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Was any medication taken to treat you for any of the gaming related injuries? <span className="text-red-500">*</span>
          {enrollmentData.medicationForGamingInjuries === 'No' && (
            <span className="ml-2 text-xs text-gray-500">
              (Auto-selected: No)
            </span>
          )}
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input 
              type="radio" 
              name="medicationForGamingInjuries" 
              value="Yes" 
              checked={enrollmentData.medicationForGamingInjuries === 'Yes'} 
              onChange={() => updateEnrollmentData('medicationForGamingInjuries', 'Yes')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="medicationForGamingInjuries" 
              value="No" 
              checked={enrollmentData.medicationForGamingInjuries === 'No'} 
              onChange={() => updateEnrollmentData('medicationForGamingInjuries', 'No')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">No</span>
          </label>
        </div>
        {errors.medicationForGamingInjuries && (
          <p className="mt-1 text-sm text-red-600">{errors.medicationForGamingInjuries}</p>
        )}
      </div>

      {/* Willing to Speak to Doctor */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          Are you willing to speak to a doctor about gaming addiction? <span className="text-red-500">*</span>
          {enrollmentData.willingToSpeakToDoctor === 'No' && (
            <span className="ml-2 text-xs text-gray-500">
              (Auto-selected: No)
            </span>
          )}
        </label>
        <div className="flex items-center space-x-4">
          <label className="flex items-center">
            <input 
              type="radio" 
              name="willingToSpeakToDoctor" 
              value="Yes" 
              checked={enrollmentData.willingToSpeakToDoctor === 'Yes'} 
              onChange={() => updateEnrollmentData('willingToSpeakToDoctor', 'Yes')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">Yes</span>
          </label>
          <label className="flex items-center">
            <input 
              type="radio" 
              name="willingToSpeakToDoctor" 
              value="No" 
              checked={enrollmentData.willingToSpeakToDoctor === 'No'} 
              onChange={() => updateEnrollmentData('willingToSpeakToDoctor', 'No')} 
              className="form-radio text-primary-600" 
            />
            <span className="ml-2 text-accent-700">No</span>
          </label>
        </div>
        {errors.willingToSpeakToDoctor && (
          <p className="mt-1 text-sm text-red-600">{errors.willingToSpeakToDoctor}</p>
        )}
      </div>

      {/* Medical Provider Info - Only show if "Yes" is selected for medical provider diagnosis */}
      {enrollmentData.medicalProviderDiagnosis === 'Yes' && (
        <>
          {/* Medical Provider Info */}
          <div>
            <label className="block text-sm font-medium text-accent-900 mb-2">Medical Provider Name / Hospital <span className="text-red-500">*</span></label>
            <input 
              type="text" 
              name="medicalProviderName"
              className="input-field w-full" 
              value={enrollmentData.medicalProviderName || ''} 
              onChange={(e) => updateEnrollmentData('medicalProviderName', e.target.value)} 
            />
            {errors.medicalProviderName && (
              <p className="mt-1 text-sm text-red-600">{errors.medicalProviderName}</p>
            )}
          </div>

          {/* Medical Provider Address */}
          <div>
            <label className="block text-sm font-medium text-accent-900 mb-2">Address of Medical Provider / Hospital <span className="text-red-500">*</span></label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="Address Line 1"
                  value={enrollmentData.medicalProviderAddress1 || ''} 
                  onChange={(e) => updateEnrollmentData('medicalProviderAddress1', e.target.value)} 
                />
                {errors.medicalProviderAddress1 && (
                  <p className="mt-1 text-sm text-red-600">{errors.medicalProviderAddress1}</p>
                )}
              </div>
              <div>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="Address Line 2"
                  value={enrollmentData.medicalProviderAddress2 || ''} 
                  onChange={(e) => updateEnrollmentData('medicalProviderAddress2', e.target.value)} 
                />
              </div>
              <div>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="City"
                  value={enrollmentData.medicalProviderCity || ''} 
                  onChange={(e) => updateEnrollmentData('medicalProviderCity', e.target.value)} 
                />
                {errors.medicalProviderCity && (
                  <p className="mt-1 text-sm text-red-600">{errors.medicalProviderCity}</p>
                )}
              </div>
              <div>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="State"
                  value={enrollmentData.medicalProviderState || ''} 
                  onChange={(e) => updateEnrollmentData('medicalProviderState', e.target.value)} 
                />
                {errors.medicalProviderState && (
                  <p className="mt-1 text-sm text-red-600">{errors.medicalProviderState}</p>
                )}
              </div>
              <div>
                <input 
                  type="text" 
                  className="input-field" 
                  placeholder="ZIP Code"
                  value={enrollmentData.medicalProviderZip || ''} 
                  onChange={(e) => updateEnrollmentData('medicalProviderZip', e.target.value)} 
                />
                {errors.medicalProviderZip && (
                  <p className="mt-1 text-sm text-red-600">{errors.medicalProviderZip}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Other Medical Conditions */}
      <div>
        <label className="block text-sm font-medium text-accent-900 mb-2">
          List any other medical conditions you have been diagnosed with in the past ten years? <span className="text-red-500">*</span>
          {enrollmentData.otherMedicalConditions === 'N/A' && (
            <span className="ml-2 text-xs text-gray-500">
              (Auto-populated: N/A)
            </span>
          )}
        </label>
        <textarea 
          className="input-field w-full min-h-[80px]" 
          value={enrollmentData.otherMedicalConditions || ''} 
          onChange={(e) => updateEnrollmentData('otherMedicalConditions', e.target.value)}
          placeholder="Please list any other medical conditions..."
        />
      </div>
    </>
  )
}
